import apiClient from './client'
import type { Project, Transcript, TranscriptAnalysis, Abbreviation } from '../types'

export const getProject = async (projectId: number): Promise<Project> => {
  const response = await apiClient.get(`/user/projects/${projectId}`)
  return response.data
}

export const getTranscript = async (projectId: number): Promise<Transcript> => {
  const response = await apiClient.get(`/user/projects/${projectId}/transcript`)
  return response.data
}

export const getAudioUrl = async (projectId: number): Promise<{ url: string; expires_in: number }> => {
  const response = await apiClient.get(`/user/projects/${projectId}/audio-url`)
  return response.data
}

export const editSegment = async (
  projectId: number,
  segmentIndex: number,
  field: 'text' | 'speaker',
  newValue: string
): Promise<{ success: boolean; version: number }> => {
  const response = await apiClient.patch(`/user/projects/${projectId}/transcript`, {
    segment_index: segmentIndex,
    field,
    new_value: newValue,
  })
  return response.data
}

export const getSpeakerNames = async (projectId: number): Promise<Record<string, string>> => {
  const response = await apiClient.get(`/user/projects/${projectId}/speakers`)
  return response.data
}

export const setSpeakerName = async (
  projectId: number,
  speakerId: string,
  name: string
): Promise<{ success: boolean }> => {
  const response = await apiClient.put(
    `/user/projects/${projectId}/speakers/${encodeURIComponent(speakerId)}`,
    null,
    { params: { name } }
  )
  return response.data
}

export const createShareLink = async (
  projectId: number,
  scope: 'view' | 'edit' = 'view',
  expiresHours: number = 72
): Promise<{ url: string; token: string; expires_at: string }> => {
  const response = await apiClient.post(
    `/user/projects/${projectId}/share`,
    null,
    { params: { scope, expires_hours: expiresHours } }
  )
  return response.data
}

export const exportTranscript = (
  transcript: Transcript,
  format: 'txt' | 'srt' = 'txt',
  speakerNames?: Record<string, string>
): string => {
  const getSpeakerName = (id: string | null) => {
    if (!id) return ''
    return speakerNames?.[id] || id
  }

  if (format === 'srt') {
    return transcript.segments.map((seg, i) => {
      const startTime = formatSrtTime(seg.start)
      const endTime = formatSrtTime(seg.end)
      const speaker = getSpeakerName(seg.speaker)
      return `${i + 1}\n${startTime} --> ${endTime}\n${speaker ? `[${speaker}] ` : ''}${seg.text}\n`
    }).join('\n')
  }

  // Plain text format
  return transcript.segments.map(seg => {
    const time = formatTime(seg.start)
    const speaker = getSpeakerName(seg.speaker)
    return `${time} ${speaker ? `[${speaker}] ` : ''}${seg.text}`
  }).join('\n')
}

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const formatSrtTime = (seconds: number): string => {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 1000)
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`
}

export const splitSegment = async (
  projectId: number,
  segmentIndex: number,
  charPosition: number
): Promise<{ success: boolean; version: number }> => {
  const response = await apiClient.post(
    `/user/projects/${projectId}/transcript/split`,
    { segment_index: segmentIndex, char_position: charPosition }
  )
  return response.data
}

// Word-level analysis API

export const getTranscriptAnalysis = async (projectId: number): Promise<TranscriptAnalysis> => {
  const response = await apiClient.get(`/user/projects/${projectId}/transcript/analysis`)
  return response.data
}

export const correctWord = async (
  projectId: number,
  segmentIndex: number,
  wordIndex: number,
  correctedTo: string
): Promise<{ success: boolean }> => {
  const response = await apiClient.patch(`/user/projects/${projectId}/transcript/words`, {
    segment_index: segmentIndex,
    word_index: wordIndex,
    corrected_to: correctedTo,
  })
  return response.data
}

export const getAbbreviations = async (projectId: number): Promise<Abbreviation[]> => {
  const response = await apiClient.get(`/user/projects/${projectId}/abbreviations`)
  return response.data
}

export const updateAbbreviationMode = async (
  projectId: number,
  abbreviationId: number,
  displayMode: 'short' | 'full' | 'both'
): Promise<{ success: boolean }> => {
  const response = await apiClient.patch(
    `/user/projects/${projectId}/abbreviations/${abbreviationId}`,
    { display_mode: displayMode }
  )
  return response.data
}
